import React, { useState, useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Tooltip, Radio, RadioChangeEvent } from "antd"
import { useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "api/queryKeys"
import { useDataImport } from "features/DataImport/api/useDataImport"

import DataImportTable from "features/DataImport/components/DataImportTable/DataImportTable.component"
import DummyDataPanel from "features/DummyData/components/DummyDataPanel.component"
import DummyBadge from "features/DummyData/components/DummyBadge.component"
import { useDummyData } from "features/DummyData/api/useDummyData"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import ProgressModal from "shared/ui/ProgressModal/ProgressModal.component"
import { errMsgOf, failureOf } from "utils/errMsgOf"

import { useProjectStore } from "stores/projectStore"
import { ROUTES } from "constants/routes"
import { PROJECT_TAB_KEYS } from "constants/projectTabs"
import { PROJECT_TYPE } from "types/project.types"
import {
  DATAFILE_TYPE,
  DataFileItem,
  CombineDataFilesResponse,
  ImportMethod,
  ImportWarning,
} from "types/dataImport.types"
import { parseImportWarnings } from "features/DataImport/utils/parseImportWarnings"
import { warnIfImportNotRecorded } from "features/DataImport/utils/warnIfImportNotRecorded"
import ImportWarningsPanel from "features/DataImport/components/ImportWarningsPanel.component"
import DatamapEditorModal from "features/DataImport/components/DatamapEditor/DatamapEditorModal.component"
import { useDatamapEditor } from "features/DataImport/api/useDatamapEditor"
import { serverMessageOf } from "features/DataImport/utils/serverMessage"
import { nothingImportedWarningsOf } from "features/DataImport/utils/nothingImported"
import { downloadCsvAsFile } from "utils/files/downloadUtils"

import { notify } from "shared/ui/Notifications/notify"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation, faCheckCircle } from "@fortawesome/free-solid-svg-icons"

/**
 * DataImportContainer component handles the logic for checking and combining data files.
 * It interacts with various hooks and components to ensure data is correctly imported and combined.
 *
 * @component
 * @param props.dataOnly - The project has no questionnaire (a data-only or
 *   dashboard project), so the "Dummy data" method is not offered.
 * @returns  The rendered DataImportContainer component.
 */
const DataImportContainer: React.FC<{ dataOnly?: boolean }> = ({
  dataOnly = false,
}) => {
  const project = useProjectStore((state) => state.project)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    getDataFilesUploaded,
    isGettingDataFilesUploaded,
    combineDataFiles,
    isCombiningDataFiles,
    generateDQDFromJSON,
    isGeneratingDQD,
    deleteDataImportFile,
  } = useDataImport()
  // Whether the project's data was generated for testing (the dummy marker).
  // A project without a questionnaire never has dummy data: nothing to read.
  const {
    isActive: hasDummyData,
    isLoading: isLoadingDummyData,
    refresh: refreshDummyData,
    readFresh: readDummyData,
  } = useDummyData(dataOnly ? undefined : project.id)
  const [dataFiles, setDataFiles] = useState<DataFileItem[]>([])
  // Whether the files were read once: until then, which files the project
  // has (its map, for the Dummy data method) is not known.
  const [filesChecked, setFilesChecked] = useState(false)
  // The last read of the files failed: before a first success, the Dummy
  // data method says so and offers to read them again.
  const [filesFailed, setFilesFailed] = useState(false)
  // "Remove dummy data": asking, then removing.
  const [askRemoveDummy, setAskRemoveDummy] = useState(false)
  const [isRemovingDummy, setIsRemovingDummy] = useState(false)
  const [importWarnings, setImportWarnings] = useState<ImportWarning[]>([])
  // `?method=dummy` (from the dummy-data links elsewhere) opens that method.
  // Dummy data walks the questionnaire, so a project without one never
  // offers it: there `?method=dummy` falls back to the default method, and
  // so does a dummy choice made before the project's type was known.
  const [searchParams, setSearchParams] = useSearchParams()
  const [importMethod, setImportMethod] = useState<ImportMethod>(() =>
    !dataOnly && searchParams.get("method") === "dummy" ? "dummy" : "csv_map"
  )
  useEffect(() => {
    if (dataOnly) setImportMethod((m) => (m === "dummy" ? "csv_map" : m))
    else if (searchParams.get("method") === "dummy") setImportMethod("dummy")
  }, [searchParams, dataOnly])
  const showsDummy = !dataOnly && importMethod === "dummy"
  const { loadDatamap, reimport } = useDatamapEditor()
  const [editor, setEditor] = useState<{ open: boolean; initialText?: string; note?: string }>({ open: false })
  const [isReimporting, setIsReimporting] = useState(false)

  /**
   * useEffect hook that triggers the checkDataFiles function when the component mounts.
   */
  useEffect(() => {
    checkDataFiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // This tab stays mounted while the others are used: dummy data generated
  // from the Tables or Dashboards tab turns the marker on, and the files
  // must be read again for the Data Ready banner. Only a change after the
  // first answer counts (the mount has just read them); reading the files
  // reads the marker again, which then holds still, so this cannot loop.
  const markerSeen = useRef<boolean | null>(null)
  useEffect(() => {
    if (isLoadingDummyData) return
    const changed = markerSeen.current !== null && markerSeen.current !== hasDummyData
    markerSeen.current = hasDummyData
    if (changed) void checkDataFiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDummyData, isLoadingDummyData])

  /**
   * Function to check if data files have been uploaded for the current project.
   * If successful, it updates the state with the data files.
   * In case of an error, it displays an error notification.
   *
   * @async
   * @function
   */
  const checkDataFiles = async (): Promise<void> => {
    // An import of real data turns the dummy marker off on the server.
    refreshDummyData()
    // The other tabs' "has data" and "has a map" answers are out of date too.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.datafileExists.project(project.id),
    })
    try {
      const csvFileRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.CSV,
        "size"
      )
      const mapFileRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.MAP,
        "size"
      )
      const jsonFileRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.JSON,
        "size"
      )
      const dqdFileRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.DQD,
        "size"
      )

      const dataFiles: DataFileItem[] = [
        {
          key: "data",
          fileType: "Data File (.data.csv / .txt)",
          isUploaded: typeof csvFileRes.data === "number",
          type: DATAFILE_TYPE.CSV,
        },
        {
          key: "map",
          fileType: "Map File (.map.csv)",
          isUploaded: typeof mapFileRes.data === "number",
          type: DATAFILE_TYPE.MAP,
        },
        {
          key: "json",
          fileType: "JSON Data File (.json)",
          isUploaded: typeof jsonFileRes.data === "number",
          type: DATAFILE_TYPE.JSON,
        },
        {
          key: "result",
          fileType: "Data(Q) Data file (.dqd)",
          isUploaded: typeof dqdFileRes.data === "number",
          type: DATAFILE_TYPE.DQD,
        },
      ]

      setDataFiles(dataFiles || [])
      setFilesChecked(true)
      setFilesFailed(false)
    } catch (error) {
      setFilesFailed(true)
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Error checking data files!", errorMessage)
    }
  }

  /**
   * Function to combine the data files if both required files (.data.csv and .map.csv) are present.
   * Displays a success notification on successful combination, otherwise displays an error notification.
   * Also extracts and displays any import warnings.
   *
   * @async
   * @function
   * @param overwrite - Re-import over the existing DQD (after the datamap editor saves).
   */
  const combineDataWithMap = async (overwrite: boolean = false): Promise<void> => {
    try {
      let combineRes: CombineDataFilesResponse
      if (overwrite) {
        // A re-import is a plain call, not the combine mutation, so it shows its own progress.
        setIsReimporting(true)
        try {
          combineRes = (await reimport(project.id, project.Created_by)) as CombineDataFilesResponse
        } finally {
          setIsReimporting(false)
        }
      } else {
        combineRes = (await combineDataFiles(project.id)) as CombineDataFilesResponse
      }

      // Handle different response formats
      let responseMessage: string | undefined
      let warnings: string | undefined

      if (typeof combineRes.data === "string") {
        // Legacy format: plain string response
        responseMessage = combineRes.data
      } else if (combineRes.data?.result) {
        // Wrapped format: { result: { message, warnings } }
        responseMessage = combineRes.data.result.message
        warnings = combineRes.data.result.warnings
      } else {
        // Direct format: { message, warnings }
        responseMessage = combineRes.data?.message
        warnings = combineRes.data?.warnings
      }

      const parsedWarnings = parseImportWarnings(warnings)
      setImportWarnings(parsedWarnings)

      if (responseMessage === "Data Combined Successfully!") {
        // Unmapped columns alone are expected on most scripting platforms, so they do not
        // downgrade the toast. Anything else is worth stopping for.
        const needsAttention = parsedWarnings.some((warning) => warning.code !== "unmapped")

        if (needsAttention) {
          notify.toast.warning("Data combined - see import results below")
        } else {
          notify.toast.success("Data combined successfully!")
        }
        warnIfImportNotRecorded(combineRes.data)

        checkDataFiles()
      } else {
        notify.error(
          "Error combining data files!",
          errMsgOf(combineRes.data) ?? responseMessage ?? "The data files could not be combined."
        )
        checkDataFiles()
      }
    } catch (combineError) {
      // When no question imported, the Conductor's 400 carries the engine's import warnings,
      // which can run to tens of KB. They belong in the import results panel, not a toast.
      const nothingImported = nothingImportedWarningsOf(combineError)
      if (nothingImported) {
        setImportWarnings(nothingImported)
        notify.error("Nothing was imported", "See the import results below.")
      } else {
        notify.error("Error combining data files!", serverMessageOf(combineError, "Unknown error occurred"))
      }
    }
  }

  const openEditor = (): void => setEditor({ open: true })
  const handleInvalidMap = (text: string, note: string): void => setEditor({ open: true, initialText: text, note })

  const handleEditorSaved = async (runImport: boolean): Promise<void> => {
    setEditor({ open: false })
    await checkDataFiles()
    if (runImport) await combineDataWithMap(true)
  }

  const downloadMap = async (): Promise<void> => {
    try {
      const { text } = await loadDatamap(project.id, project.Created_by)
      downloadCsvAsFile(text, `${project.Name || "datamap"}.map.csv`)
    } catch (error) {
      notify.error("The datamap could not be downloaded", serverMessageOf(error, "Unknown error"))
    }
  }

  /**
   * Function to generate a DQD file from an uploaded JSON data file.
   * Displays a success notification on successful generation, otherwise displays an error notification.
   *
   * @async
   * @function
   */
  const generateDQD = async (): Promise<void> => {
    try {
      const generateRes = (await generateDQDFromJSON(project.id)) as CombineDataFilesResponse

      // Handle different response formats
      let responseMessage: string | undefined

      if (typeof generateRes.data === "string") {
        responseMessage = generateRes.data
      } else if (generateRes.data?.result) {
        responseMessage = generateRes.data.result.message
      } else {
        responseMessage = generateRes.data?.message
      }

      if (responseMessage === "DQD file generated successfully from JSON!") {
        notify.toast.success("DQD file generated successfully!")
        warnIfImportNotRecorded(generateRes.data)
        checkDataFiles()
      } else {
        notify.error(
          "Error generating DQD file!",
          errMsgOf(generateRes.data) ?? responseMessage ?? "The DQD file could not be generated."
        )
        checkDataFiles()
      }
    } catch (error) {
      // A refused save (an error status) carries the server's message.
      notify.error("Error generating DQD file!", failureOf(error))
    }
  }

  /**
   * Removes the dummy data: its CSV, then its DQD (the server reads the dummy
   * marker from the DQD's own metadata, so it goes with the DQD). The map
   * stays for the real data. An upload never replaces a file, so real data
   * cannot come in until the dummy files are gone. The CSV goes first: if the
   * DQD then fails, the data is still marked as dummy and the action stays to
   * try again.
   *
   * The marker this screen shows may be out of date (real data imported from
   * elsewhere, or the files replaced by another writer), so it is read again
   * from the server first, and nothing is removed unless the data is still
   * dummy data. The DQD is then the run's and always goes; the CSV goes only
   * while the server says it is still the run's own file (`csvIsDummy`: the
   * same SHA-256 as the run's CSV), so a real CSV that replaced it stays.
   */
  const removeDummyData = async (): Promise<void> => {
    setAskRemoveDummy(false)
    setIsRemovingDummy(true)
    try {
      const now = await readDummyData({ csv: true })
      if (!now.active) {
        notify.error(
          "Nothing was removed",
          "This project's data is no longer marked as dummy data."
        )
        return
      }
      const dummyFiles = now.csvIsDummy
        ? [DATAFILE_TYPE.CSV, DATAFILE_TYPE.DQD]
        : [DATAFILE_TYPE.DQD]
      for (const type of dummyFiles) {
        const res = await deleteDataImportFile(project.id, project.Created_by, type)
        const msg = errMsgOf(res.data)
        if (msg !== null) throw new Error(msg)
      }
      notify.toast.success("Dummy data removed")
    } catch (e) {
      notify.error("Could not remove the dummy data", failureOf(e))
    } finally {
      setIsRemovingDummy(false)
      // Reads the files and the dummy marker again, whatever was removed.
      void checkDataFiles()
    }
  }

  const hasMapFile = dataFiles.some(
    (file) => file.type === DATAFILE_TYPE.MAP && file.isUploaded
  )

  const hasCsvAndMap =
    dataFiles.some(
      (file) => file.type === DATAFILE_TYPE.CSV && file.isUploaded
    ) && hasMapFile

  const hasJsonFile = dataFiles.some(
    (file) => file.type === DATAFILE_TYPE.JSON && file.isUploaded
  )

  const hasDqdFile = dataFiles.some(
    (file) => file.type === DATAFILE_TYPE.DQD && file.isUploaded
  )

  // Data-only projects have no routing to check against, so the Data Checks tab is hidden for
  // them (ProjectDetailsTabs.container) — pointing at it there would land on nothing.
  // Case-normalised: the tab filter reads the server-canonical project type, while this is the
  // raw metadata value, which the General Information form round-trips upper-cased. Comparing
  // verbatim would show this link on a project whose tab is hidden (house precedent: WaveCompare,
  // TrackerManager).
  const canRunBaseCheck = project.Type.toLowerCase() !== PROJECT_TYPE.DATA_ONLY

  const isProcessing = isCombiningDataFiles || isGeneratingDQD || isReimporting

  // Filter data files based on selected import method
  const filteredDataFiles = dataFiles.filter((file) => {
    switch (importMethod) {
      case "csv_map":
        return file.type === DATAFILE_TYPE.CSV || file.type === DATAFILE_TYPE.MAP
      case "json":
        return file.type === DATAFILE_TYPE.JSON
      case "dqd":
        return file.type === DATAFILE_TYPE.DQD
      default:
        return true
    }
  })

  const handleImportMethodChange = (e: RadioChangeEvent) => {
    setImportMethod(e.target.value as ImportMethod)
    setImportWarnings([]) // Clear warnings when switching methods
    // Once the user picks a method, `?method=` has done its job. Left in the
    // URL, the next change to the URL (leaving the tab drops `?tab=`) would
    // switch the method back to dummy data.
    if (searchParams.has("method"))
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete("method")
          return next
        },
        { replace: true }
      )
  }

  return (
    <>
      <ProgressModal
        title={isGeneratingDQD ? "Generating DQD File" : "Combining Data Files"}
        isLoading={isProcessing}
        duration={3200}
      />
      <div className="flex flex-col h-full overflow-y-auto w-full">
        {/* Import Method Selection */}
        <div className="p-4 border-b border-gray-200">
          <p className="text-sm text-gray-600 mb-2">Choose your import method:</p>
          {/* A view switch, not an action: outlined, on one line that never wraps. */}
          <div className="dq-import-methods flex flex-nowrap items-center min-w-0">
            <Radio.Group
              value={importMethod}
              onChange={handleImportMethodChange}
              optionType="button"
              className="whitespace-nowrap"
            >
              <Radio.Button value="csv_map">
                CSV + Map Files
              </Radio.Button>
              <Radio.Button value="json">
                JSON Data File
              </Radio.Button>
              <Radio.Button value="dqd">
                DQD File (Direct)
              </Radio.Button>
              {!dataOnly && (
                <Radio.Button value="dummy">Dummy data</Radio.Button>
              )}
            </Radio.Group>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {importMethod === "csv_map" && "Upload a data file (.data.csv / .txt) and map file (.map.csv), then combine them to create a DQD file."}
            {importMethod === "json" && "Upload a Data(Q) JSON file and convert it to DQD format."}
            {importMethod === "dqd" && "Upload a DQD file directly if you already have one."}
            {showsDummy && "Generate test data from the questionnaire to try tables and dashboards before real data arrives."}
          </p>
        </div>

        {showsDummy ? (
          <DummyDataPanel
            projectId={project.id}
            owner={project.Created_by}
            projectName={project.Name}
            hasMap={
              filesChecked ? hasMapFile : filesFailed ? "failed" : "unknown"
            }
            onRetryFiles={() => void checkDataFiles()}
            onGenerated={checkDataFiles}
          />
        ) : (
          /* File Upload Table - filtered by selected method. Real data
             cannot be uploaded over dummy data: it is removed first. */
          <DataImportTable
            refresh={checkDataFiles}
            isLoading={isGettingDataFilesUploaded}
            dataFiles={filteredDataFiles}
            uploadsBlocked={hasDummyData}
            onEditMap={openEditor}
            onDownloadMap={downloadMap}
            onInvalidMap={handleInvalidMap}
          />
        )}

        {/* Show Combine button for CSV + Map method; hidden while an import runs, so a plain
            combine can't race a re-import from the datamap editor */}
        {importMethod === "csv_map" && !hasDqdFile && !hasDummyData && hasCsvAndMap && !isProcessing && (
          <div className="flex p-5 ml-auto">
            <DataQButton type="primary" onClick={() => combineDataWithMap()}>
              Combine Data Files
            </DataQButton>
            <Tooltip
              title="Combine the data and map files to generate the Data(Q) data file."
              color={"black"}
              className="ml-2 my-auto"
            >
              <FontAwesomeIcon
                icon={faCircleExclamation}
                color="gold"
                className="cursor-pointer"
              />
            </Tooltip>
          </div>
        )}

        {/* Show Generate DQD button for JSON method */}
        {importMethod === "json" && !hasDqdFile && !hasDummyData && hasJsonFile && (
          <div className="flex p-5 ml-auto">
            <DataQButton type="primary" onClick={generateDQD}>
              Generate DQD
            </DataQButton>
            <Tooltip
              title="Convert the JSON data file to Data(Q) DQD format."
              color={"black"}
              className="ml-2 my-auto"
            >
              <FontAwesomeIcon
                icon={faCircleExclamation}
                color="gold"
                className="cursor-pointer"
              />
            </Tooltip>
          </div>
        )}

        {/* Show success message when DQD is ready */}
        {hasDqdFile && (
          <div className="mx-4 mt-4">
            <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="text-emerald-500 text-lg"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-emerald-800 m-0">Data Ready</p>
                  {/* The line below says what dummy data is: no tooltip repeating it. */}
                  <DummyBadge projectId={dataOnly ? undefined : project.id} tooltip={false} />
                </div>
                <p className="text-xs text-emerald-600 m-0">
                  {hasDummyData
                    ? "This project's data was generated for testing. Remove it before importing real data."
                    : "Your DQD file is uploaded and ready for reporting."}
                </p>
                {/* The "what next" line already lives here, so the second next step costs no new
                    chrome. Not the warnings panel: it renders nothing when an import is clean —
                    exactly the case where this pointer matters most — and it is dismissible. */}
                {canRunBaseCheck && (
                  <p className="text-xs text-emerald-700 m-0 mt-1">
                    You can also check the imported data against the questionnaire’s routing on the{" "}
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`${ROUTES.PROJECT(project.id)}?tab=${PROJECT_TAB_KEYS.DATA_CHECKS}`)
                      }
                      className="underline hover:text-emerald-900"
                    >
                      Data Checks
                    </button>{" "}
                    tab.
                  </p>
                )}
              </div>
              {hasDummyData && !dataOnly && (
                <DataQButton
                  type="secondary"
                  size="small"
                  isLoading={isRemovingDummy}
                  onClick={() => setAskRemoveDummy(true)}
                >
                  Remove dummy data
                </DataQButton>
              )}
            </div>
          </div>
        )}
        <ConfirmationModal
          title="Remove the dummy data?"
          isVisible={askRemoveDummy}
          message="The map stays, so real data can be imported with it."
          confirmButtonText="Remove"
          confirmButtonType="danger"
          cancel={() => setAskRemoveDummy(false)}
          confirm={() => void removeDummyData()}
        />

        {/* Import Results Panel */}
        <ImportWarningsPanel
          warnings={importWarnings}
          onDismiss={() => setImportWarnings([])}
          onOpenEditor={hasMapFile ? openEditor : undefined}
        />
      </div>
      <DatamapEditorModal
        open={editor.open}
        projectId={project.id}
        owner={project.Created_by}
        projectName={project.Name}
        initialText={editor.initialText}
        note={editor.note}
        onClose={() => setEditor({ open: false })}
        onSaved={(runImport) => void handleEditorSaved(runImport)}
      />
    </>
  )
}

export default DataImportContainer
